/**
 * SecuritySetupModal
 * ──────────────────────────────────────────────────────────────────────────────
 * Opened from AppSettingsScreen's "Security & Account" section.
 *
 * Flow:
 *   PIN Gate → [if no PIN: Create PIN | if PIN exists: Verify PIN]
 *   → Create/Edit Security Questions
 *   → Save & Confirm
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    X, ShieldCheck, KeyRound, HelpCircle, CheckCircle2,
    AlertCircle, ArrowLeft, Save, Eye, EyeOff, ChevronDown, Lock
} from 'lucide-react';
import { getAuth, updatePassword } from 'firebase/auth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useUserData } from './UserDataContext';
import { triggerHaptic } from '../utils/haptic';
import {
    SECURITY_QUESTIONS,
    hasPIN,
    setPIN,
    verifyPIN,
    setSecurityQuestionsWithExisting,
    SecurityQuestionInput,
} from '../services/recoveryService';
import { toast } from 'sonner';
import { Dialog, DialogPortal, DialogOverlay } from './ui/dialog';
import { MODAL, MODAL_ACCENT, MODAL_SPINNER, modalPinBox, modalPrimaryButton } from './ui/unifiedModalStyle';

interface SecuritySetupModalProps {
    onClose: () => void;
    showSkip?: boolean;
}

type Step = 'pin_gate' | 'pin_create' | 'pin_confirm' | 'questions' | 'done';

// ── 4-digit PIN input component ───────────────────────────────────────────────
/** Re-exported name kept local so the call sites below stay readable. */
const PIN_ACCENT = MODAL_ACCENT;

/** Example question per row. Placeholders only: nothing is pre-filled. */
const QUESTION_EXAMPLES = [
    'e.g. what city was I born in?',
    "e.g. what was my favorite teacher's name?",
];

function PinInput({
    value,
    onChange,
    disabled = false,
    label,
}: {
    value: string;
    onChange: (val: string) => void;
    disabled?: boolean;
    label?: string;
}) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !value[index] && index > 0) {
            const next = value.slice(0, index - 1) + value.slice(index);
            onChange(next);
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleChange = (index: number, char: string) => {
        if (!/^\d$/.test(char)) return;
        const arr = value.split('').concat(Array(4).fill('')).slice(0, 4);
        arr[index] = char;
        const newVal = arr.join('').replace(/\s/g, '').slice(0, 4);
        onChange(newVal);
        if (index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const digits = (value + '    ').split('').slice(0, 4);
    // Which box the caret is in. Tracked in state rather than read from :focus,
    // because the focused style has to be an inline style like everything else
    // here — src/index.css is pre-compiled, so a focus: variant that is not
    // already in that bundle renders nothing.
    const [focused, setFocused] = useState<number | null>(null);

    return (
        <div>
            {label && (
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12, textAlign: 'center' }}>
                    {label}
                </p>
            )}
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
                {digits.map((digit, i) => {
                    const filled = !!digit.trim();
                    const isFocused = focused === i;
                    return (
                        <input
                            key={i}
                            ref={(el) => { inputRefs.current[i] = el; }}
                            type="password"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit.trim()}
                            disabled={disabled}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onFocus={(e) => { setFocused(i); e.target.select(); }}
                            onBlur={() => setFocused(f => (f === i ? null : f))}
                            className="text-center outline-none"
                            style={modalPinBox(filled, isFocused)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

/**
 * The body shared by "Create PIN" and "Confirm PIN".
 *
 * One component for both so the confirm step cannot drift from the create step
 * again — they were separate copies with different spacing, different heading
 * sizes and differently shaped buttons.
 *
 * Spacing and type follow YapTabGuidelinesModal, the reference for the app's
 * modal look: 24px content padding, 20px bold heading, 14px body at 1.5, and a
 * 56px full-width primary button with a 20px radius. Inline styles throughout,
 * because that modal is inline-styled for the same reason everything here is —
 * src/index.css is committed pre-compiled Tailwind.
 */
function CreatePinBody({
    pin,
    setPin,
    onContinue,
    onSkip,
    heading = 'Protect your account',
    helper = "Pick a 4-digit PIN you'll remember. You'll need it to edit your account details.",
    cta = 'Continue',
    busy = false,
    error,
}: {
    pin: string;
    setPin: (v: string) => void;
    onContinue: () => void;
    /** Omitted entirely when skipping is not offered. */
    onSkip?: () => void;
    heading?: string;
    helper?: string;
    cta?: string;
    busy?: boolean;
    error?: string;
}) {
    const ready = pin.length === 4 && !busy;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 4 }}>
            <h3 style={MODAL.heading}>
                {heading}
            </h3>

            <p style={MODAL.body}>
                {helper}
            </p>

            <div style={{ marginTop: 26 }}>
                <PinInput value={pin} onChange={setPin} />
            </div>

            {error && (
                <p style={{ fontSize: 13, color: '#f87171', textAlign: 'center', margin: '14px 0 0' }}>
                    {error}
                </p>
            )}

            <button
                type="button"
                onClick={onContinue}
                disabled={!ready}
                style={{ ...modalPrimaryButton(ready), marginTop: 26 }}
            >
                {busy
                    ? <span className="animate-spin" style={MODAL_SPINNER} />
                    : cta}
            </button>

            {onSkip && (
                // Below Continue, and a text link rather than a bordered pill: it
                // used to be a prominent outlined button ABOVE the heading, which
                // made skipping look like the main action.
                <button
                    type="button"
                    onClick={onSkip}
                    style={MODAL.secondaryLink}
                >
                    Skip for now
                </button>
            )}
        </div>
    );
}

// ── Question row ──────────────────────────────────────────────────────────────
function QuestionRow({
    index,
    question,
    answer,
    onQuestionChange,
    onAnswerChange,
}: {
    index: number;
    question: string;
    answer: string;
    onQuestionChange: (q: string) => void;
    onAnswerChange: (a: string) => void;
}) {
    const [showAnswer, setShowAnswer] = useState(false);
    const hasSavedQuestion = question.trim().length > 0 && answer.length === 0;
    // A different example per row, so the second field does not look like a
    // repeat of the first.
    const example = QUESTION_EXAMPLES[index] ?? QUESTION_EXAMPLES[0];

    return (
        <div style={{
            width: '100%',
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
        }}>
            {/* Each pair is grouped in its own card, so a question and its answer
                read as belonging together. They used to be two blocks 40px apart
                inside a borderless row 64px from the next one, which made the
                labels look sandwiched between unrelated fields. */}
            <p style={MODAL.label}>
                Question {index + 1}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                    placeholder={example}
                    value={question}
                    onChange={(e) => onQuestionChange(e.target.value)}
                    className="outline-none"
                    style={MODAL.input}
                />

                <div style={{ position: 'relative' }}>
                    <Input
                        type={showAnswer ? 'text' : 'password'}
                        placeholder={hasSavedQuestion ? 'Re-enter to change' : 'Your answer'}
                        value={answer}
                        onChange={(e) => onAnswerChange(e.target.value)}
                        autoCapitalize="none"
                        autoCorrect="off"
                        className="outline-none"
                        style={{ ...MODAL.input, paddingRight: 44 }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowAnswer((v) => !v)}
                        aria-label={showAnswer ? 'Hide answer' : 'Show answer'}
                        style={{
                            position: 'absolute', right: 12, top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none', border: 'none', padding: 0,
                            color: 'rgba(255,255,255,0.45)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center',
                        }}
                    >
                        {showAnswer ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {hasSavedQuestion && (
                <p style={{
                    margin: 0, fontSize: 12, lineHeight: 1.4,
                    color: 'rgba(255,255,255,0.40)',
                }}>
                    Answer saved. Re-enter only to change it.
                </p>
            )}
        </div>
    );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function SecuritySetupModal({ onClose, showSkip = false }: SecuritySetupModalProps) {
    const { userProfile } = useUserData();
    const userId = String(userProfile?.id || '');
    const username = userProfile?.username || '';

    const [step, setStep] = useState<Step>('pin_gate');
    const [pinExists, setPinExists] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // PIN state
    const [pin, setPin] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');

    // Password reset state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState({ text: '', type: '' });

    // Questions state (2 questions)
    const [questions, setQuestions] = useState<SecurityQuestionInput[]>([
        { question: '', answer: '' },
        { question: '', answer: '' },
    ]);

    // Check if PIN already set on mount, and load existing questions
    useEffect(() => {
        const check = async () => {
            if (!userId) { setLoading(false); return; }
            const has = await hasPIN(userId);
            setPinExists(has);
            setLoading(false);
        };
        check();
    }, [userId]);

    // When user reaches the questions step, pre-fill existing questions if any
    useEffect(() => {
        if (step !== 'questions') return;
        const existing = userProfile?.security_questions;
        if (existing && existing.length > 0) {
            setQuestions(existing.map((q) => ({
                question: q.question,
                answer: '', // answers are hashed — cannot be decrypted, user re-enters to change
            })).concat(
                // pad to 2 slots if only 1 question was saved
                existing.length < 2 ? [{ question: '', answer: '' }] : []
            ).slice(0, 2));
        }
    }, [step]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    // Verify existing PIN to unlock
    const handleVerifyPIN = async () => {
        if (pin.length !== 4) return;
        setLoading(true);
        setErrorMsg('');
        const valid = await verifyPIN(userId, pin);
        setLoading(false);
        if (valid) {
            setPin('');
            setStep('questions');
        } else {
            setErrorMsg('Incorrect PIN. Please try again.');
        }
    };

    // Move from create PIN to confirm
    const handlePinCreateNext = () => {
        if (pin.length !== 4) return;
        setErrorMsg('');
        setStep('pin_confirm');
    };

    // Confirm PIN matches and proceed to questions
    const handlePinConfirmNext = async () => {
        if (pinConfirm.length !== 4) return;
        if (pin !== pinConfirm) {
            setErrorMsg('PINs do not match. Please try again.');
            setPinConfirm('');
            return;
        }
        setLoading(true);
        setErrorMsg('');
        try {
            await setPIN(userId, pin);
            setPinExists(true);
            setStep('questions');
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to save PIN.');
        } finally {
            setLoading(false);
        }
    };

    // Save everything
    const handleSave = async () => {
        const existingQuestions = userProfile?.security_questions || [];

        // Build the final list: questions that have a question text filled in
        // If answer is blank AND there's an existing saved hash for that slot, keep the old hash.
        const questionsToSave: SecurityQuestionInput[] = [];
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.question.trim()) continue; // skip empty question slots
            if (q.answer.trim()) {
                // User provided a new answer — use it
                questionsToSave.push({ question: q.question, answer: q.answer });
            } else if (existingQuestions[i]?.question === q.question && existingQuestions[i]?.answer_hash) {
                // Question unchanged, no new answer — preserve existing hash via a sentinel
                questionsToSave.push({ question: q.question, answer: `__KEEP_HASH__${existingQuestions[i].answer_hash}` });
            } else {
                // New question with no answer — require it
                setErrorMsg(`Please enter an answer for Question ${i + 1}.`);
                return;
            }
        }

        if (questionsToSave.length < 1) {
            setErrorMsg('Please fill in at least one security question and answer.');
            return;
        }
        setSaving(true);
        setErrorMsg('');
        try {
            // Save PIN if we just created one
            if (!pinExists) {
                await setPIN(userId, pin);
            }
            // Save questions (hashed, with sentinel passthrough for unchanged answers)
            await setSecurityQuestionsWithExisting(userId, username, questionsToSave);
            triggerHaptic('success'); // 📳 iOS parity: haptic on successful security setup
            setStep('done');
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setPasswordMsg({ text: 'Password must be at least 6 characters.', type: 'error' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordMsg({ text: 'Passwords do not match.', type: 'error' });
            return;
        }
        setPasswordSaving(true);
        setPasswordMsg({ text: '', type: '' });
        try {
            const auth = getAuth();
            if (!auth.currentUser) throw new Error("No authenticated user.");
            await updatePassword(auth.currentUser, newPassword);
            setPasswordMsg({ text: 'Password successfully updated!', type: 'success' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setPasswordMsg({ text: err?.message || 'Failed to update password.', type: 'error' });
        } finally {
            setPasswordSaving(false);
        }
    };

    const updateQuestion = (i: number, field: 'question' | 'answer', val: string) => {
        setQuestions((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], [field]: val };
            return next;
        });
    };

    const usedQuestions = questions.map((q) => q.question);

    const stepLabel = {
        pin_gate: pinExists ? 'Enter PIN' : 'Create PIN',
        pin_create: 'Create PIN',
        pin_confirm: 'Confirm PIN',
        questions: 'Security Questions',
        done: 'All Set!',
    }[step];

    const stepIndex = { pin_gate: 0, pin_create: 0, pin_confirm: 1, questions: 2, done: 3 }[step];

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogPortal>
                {/* z-index is set INLINE, not as `z-99999`.
                    src/index.css is committed pre-compiled Tailwind and contains
                    no such class — only z-0/10/20/40/50 exist in that bundle — so
                    `z-99999` compiled to nothing and this modal has been running
                    with no stacking context of its own at all. It looked fine
                    only because nothing else claimed a high one, until the app
                    guide's overlay (99000000) started rendering over the top of
                    it. The PIN prompt gates account recovery and must sit above
                    everything else that can appear at startup. */}
                <div
                    className="fixed inset-0 flex items-center justify-center p-4 overflow-hidden"
                    style={{ zIndex: 100000000 }}
                >
                    <DialogOverlay 
                        className="fixed inset-0 z-0" 
                        style={{ backdropFilter: 'blur(14px)', backgroundColor: 'rgba(0,0,0,0.7)' }} 
                    />
                    
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', stiffness: 290, damping: 26 }}
                        className="relative z-10 w-full rounded-3xl overflow-hidden shadow-2xl"
                        style={{
                            background: 'linear-gradient(160deg, rgba(15,15,22,0.99) 0%, rgba(5,5,10,1) 100%)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0 40px 100px rgba(0,0,0,0.8), inset 0 1px 1px rgba(255,255,255,0.05)',
                            // Shell sizing, dvh and scroll behaviour: unifiedModalStyle.
                            ...MODAL.shell,
                        }}
                    >
                        {/* Header: 24px side padding, matching the reference modal. */}
                        <div className="flex items-center justify-between sticky top-0 z-10"
                            style={MODAL.header}
                        >
                            <div className="flex items-center gap-3">
                                {(step === 'pin_confirm' || step === 'questions') && (
                                    <button
                                        onClick={() => {
                                            if (step === 'pin_confirm') { setStep('pin_create'); setPinConfirm(''); setErrorMsg(''); }
                                            else if (step === 'questions') { setStep(pinExists ? 'pin_gate' : 'pin_confirm'); setErrorMsg(''); }
                                        }}
                                        className="text-zinc-400 hover:text-white transition-colors p-1 -ml-1"
                                    >
                                        <ArrowLeft className="w-5 h-5" />
                                    </button>
                                )}
                                <div className="flex items-center gap-4">
                                    <ShieldCheck className="w-5 h-5 text-[#4478ea]" />
                                    <h2 className="text-white font-bold text-lg tracking-tight ml-1">{stepLabel}</h2>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-all"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                {/* Progress dots */}
                {step !== 'done' && (
                    <div className="flex items-center gap-1.5 px-5 pb-4">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-1 rounded-full transition-all duration-300"
                                style={{
                                    flex: i === Math.min(stepIndex, 2) ? 2 : 1,
                                    background: i <= Math.min(stepIndex, 2)
                                        ? 'linear-gradient(90deg, #4478ea, #2d1ef1)'
                                        : 'rgba(255,255,255,0.08)',
                                }}
                            />
                        ))}
                    </div>
                )}

                <div
                    className="custom-scrollbar"
                    style={MODAL.content}
                >
                    <AnimatePresence mode="wait">

                        {/* ── PIN Gate (verify existing) ── */}
                        {step === 'pin_gate' && !loading && (
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                className="flex flex-col items-center pt-0 pb-12"
                            >
                                {pinExists ? (
                                    <>
                                        <div className="text-center space-y-2 w-[90%] mx-auto mt-0">
                                            <p className="text-white text-base font-bold leading-tight px-4">
                                                Enter your recovery PIN to access or edit security questions.
                                            </p>
                                        </div>

                                        <div className="h-10" />

                                        <div className="flex flex-col items-center w-full">
                                            <PinInput
                                                value={pin}
                                                onChange={setPin}
                                            />
                                        </div>

                                        {errorMsg && (
                                            <motion.p
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-red-400 text-xs text-center mt-4"
                                            >
                                                {errorMsg}
                                            </motion.p>
                                        )}

                                        <div className="h-10" />

                                        <div className="flex justify-center w-full">
                                            <Button
                                                onClick={handleVerifyPIN}
                                                disabled={pin.length !== 4 || loading}
                                                className="w-[120px] h-11 rounded-full font-bold text-white border-none shadow-lg shadow-blue-600/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                                                style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
                                            >
                                                <KeyRound className="w-4 h-4" />
                                                Unlock
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <CreatePinBody
                                        pin={pin}
                                        setPin={setPin}
                                        onContinue={handlePinCreateNext}
                                        onSkip={showSkip ? onClose : undefined}
                                    />
                                )}
                            </motion.div>
                        )}

                        {/* ── PIN Create (no PIN yet, redirect from gate) ── */}
                        {step === 'pin_create' && (
                            <motion.div
                                key="pin_create"
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                className="space-y-5 flex flex-col items-center"
                            >
                                <div className="text-center space-y-2 py-2">
                                    <p className="text-white text-base font-bold leading-tight">
                                        Create a 4-digit PIN to protect your account.
                                    </p>
                                    <p className="text-zinc-400 text-xs leading-relaxed max-w-[260px] mx-auto">
                                        Make sure it's something you can remember, you'll need this to edit any account details.
                                    </p>
                                </div>
                                <PinInput value={pin} onChange={setPin} label="Enter PIN" />
                                <Button
                                    onClick={handlePinCreateNext}
                                    disabled={pin.length !== 4}
                                    className="w-[200px] h-12 rounded-xl font-bold text-white border-none mt-8 shadow-[0_10px_30px_rgba(68,120,234,0.2)] hover:shadow-[0_10px_40px_rgba(68,120,234,0.3)] transition-all active:scale-95"
                                    style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                >
                                    Continue
                                </Button>
                            </motion.div>
                        )}

                        {/* ── PIN Confirm ── */}
                        {step === 'pin_confirm' && (
                            <motion.div
                                key="pin_confirm"
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                className="flex flex-col items-center"
                            >
                                <CreatePinBody
                                    pin={pinConfirm}
                                    setPin={setPinConfirm}
                                    onContinue={handlePinConfirmNext}
                                    heading="Confirm your PIN"
                                    helper="Re-enter the same 4-digit PIN to make sure it is correct."
                                    cta="Finish"
                                    busy={loading}
                                    error={errorMsg}
                                />
                            </motion.div>
                        )}

                        {/* ── Security Questions ── */}
                        {step === 'questions' && (
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                className="flex flex-col items-center"
                            >
                                <h3 style={MODAL.heading}>
                                    Set your security questions
                                </h3>
                                <p style={MODAL.body}>
                                    These let you recover your account. Answers are case-sensitive and hashed before they are saved.
                                </p>

                                {/* The tip and the SHA-256 note were two separate
                                    full-width panels with 48px gaps around them.
                                    One notice now, styled like the reference
                                    modal's amber warning. */}
                                <div style={{ ...MODAL.notice, marginTop: 18 }}>
                                    <p style={{
                                        fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'center',
                                        color: 'rgba(252,211,77,0.9)',
                                    }}>
                                        Tip: keep questions and answers all lowercase so they are easier to match later.
                                    </p>
                                </div>

                                {/* space-y-16 put 64px between each row. */}
                                <div style={{
                                    marginTop: 20, width: '100%',
                                    display: 'flex', flexDirection: 'column', gap: 20,
                                }}>
                                    {questions.map((q, i) => (
                                        <QuestionRow
                                            key={i}
                                            index={i}
                                            question={q.question}
                                            answer={q.answer}
                                            onQuestionChange={(val) => updateQuestion(i, 'question', val)}
                                            onAnswerChange={(val) => updateQuestion(i, 'answer', val)}
                                        />
                                    ))}
                                </div>

                                <p style={{
                                    marginTop: 16, fontSize: 12, lineHeight: 1.5,
                                    color: 'rgba(255,255,255,0.38)', textAlign: 'center',
                                }}>
                                    Answers are hashed with SHA-256 before storage. AfroYap cannot read them.
                                </p>

                                {errorMsg && (
                                    <motion.p
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        style={{ ...MODAL.error, marginTop: 14 }}
                                    >
                                        <AlertCircle className="w-4 h-4 shrink-0" style={{ marginTop: 1 }} />
                                        {errorMsg}
                                    </motion.p>
                                )}

                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    style={modalPrimaryButton(!saving)}
                                >
                                    {saving
                                        ? <span
                                            className="animate-spin"
                                            style={MODAL_SPINNER}
                                          />
                                        : <><Save className="w-4 h-4" />Save</>}
                                </button>

                                {showSkip && (
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        style={MODAL.secondaryLink}
                                    >
                                        Skip for now
                                    </button>
                                )}
                            </motion.div>
                        )}

                        {/* ── Done ── */}
                        {step === 'done' && (
                            <motion.div
                                key="done"
                                initial={{ opacity: 0, scale: 0.88 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                                className="flex flex-col items-center text-center py-10"
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.12, type: 'spring', stiffness: 300, damping: 20 }}
                                    className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
                                    style={{ background: 'linear-gradient(135deg, rgba(68,120,234,0.15), rgba(45,30,241,0.12))' }}
                                >
                                    <ShieldCheck className="w-10 h-10 text-[#4478ea]" />
                                </motion.div>
                                <h3 className="text-white font-bold text-xl mb-3">Account Secured!</h3>
                                <p className="text-zinc-400 text-sm leading-relaxed mb-8 max-w-[280px]">
                                    Your recovery PIN and security questions have been saved. You can use them to recover your account from the login screen.
                                </p>
                                <Button
                                    onClick={onClose}
                                    className="w-full h-12 rounded-xl font-bold text-white border-none shadow-lg shadow-blue-500/20"
                                    style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                >
                                    Done
                                </Button>
                            </motion.div>
                        )}

                        {loading && step === 'pin_gate' && (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex items-center justify-center py-12"
                            >
                                <div className="w-8 h-8 rounded-full border-2 border-[#4478ea]/30 border-t-[#4478ea] animate-spin" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
